import type { MarketField } from './stitchedColumnPlan';
import { StitchedColumns } from './StitchedColumns';
import type { StitchedColumnsOptions } from './StitchedColumns';
import type { KeyedTableModel } from './TableModelRowBackend';
import { TickerFeed } from './TickerFeed';
import type { TickerTableModel } from './TickerFeed';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
/**
 * Application wiring for client-stitched columns (ADR 0020): the factory a widget model calls to
 * stitch its grid, and a shared market provider for when market columns can come from per-symbol
 * feeds again.
 *
 * The application wires no market provider today: table-cpp-proxy caps a connection at 200
 * subscriptions, so per-symbol ticker tables exhaust it, and each widget's column table serves its
 * market columns instead (DEC-TABLES-001). With a provider, one serves every stitched grid, so a
 * symbol shown in several widgets has one ticker subscription (AC-SOURCE-001). Released
 * subscriptions idle for MarketDataService's two minutes, and the least recently released idle
 * ones close beyond 500 open subscriptions (DEC-RETENTION-001). Window departures wait 1.5 s for
 * the next keys batch, as in the approved simulator (OD-SCROLL-001).
 */
import type { ColumnProvider } from '@thinkalpha/ui-keyed-columns/contracts';
import { SharedKeyedProvider } from '@thinkalpha/ui-keyed-columns/sharedKeyedProvider';
import type { ActiveUserModel } from 'src/models/ActiveUserModel';

export const STITCHED_MARKET_RETENTION_MS = 120_000;
export const STITCHED_MARKET_RETENTION_LIMIT = 500;
export const STITCHED_DEPARTURE_TIMEOUT_MS = 1_500;

/** The role that entitles a user to equity market data, as MarketDataService checks it. */
const EQUITY_MARKET_DATA_ROLE = 'market-data::equity';

export type StitchedMarketProvider = SharedKeyedProvider<string, MarketField, unknown>;

/** Schedules on the timer queue; disposing cancels. */
export function scheduleTimeout(delayMs: number, run: () => void): Disposable {
    const handle = setTimeout(run, delayMs);
    return {
        [Symbol.dispose]: () => clearTimeout(handle),
    };
}

export function createStitchedMarketProvider(createTable: () => TickerTableModel, log: Logger): StitchedMarketProvider {
    return new SharedKeyedProvider<string, MarketField, unknown>(new TickerFeed(createTable), {
        retention: {
            durationMs: STITCHED_MARKET_RETENTION_MS,
            limit: STITCHED_MARKET_RETENTION_LIMIT,
            schedule: scheduleTimeout,
        },
        onError: (symbol, error) => {
            log.warn(`Stitched market data for ${symbol} failed (${error.kind})`, {
                symbol,
                kind: error.kind,
                cause: error.kind === 'failed' ? error.cause : undefined,
            });
        },
    });
}

/**
 * Whether the user may receive equity market data; superuser mode always may. A point-in-time
 * read: callers check once per grid, and the read never subscribes a surrounding effect.
 */
export function hasEquityMarketData(user: Pick<ActiveUserModel, 'userRoles' | 'isSuperUserModeEnabled'>): boolean {
    return user.isSuperUserModeEnabled.peek() || user.userRoles.peek().includes(EQUITY_MARKET_DATA_ROLE);
}

export type StitchedColumnsFactory = (
    options: Pick<StitchedColumnsOptions, 'plan' | 'columnOptions' | 'devtoolsName'>,
) => StitchedColumns;

export interface StitchedColumnsFactoryDeps {
    readonly createTable: () => KeyedTableModel;
    /**
     * A shared market provider and whether the user may receive it, checked once per grid. Omit,
     * or deny, to serve market columns from each widget's column table, where the server applies
     * entitlements as it does for a full table.
     */
    readonly market?: {
        readonly provider: () => ColumnProvider<string, MarketField, unknown>;
        readonly entitled: () => boolean;
    };
}

export function createStitchedColumnsFactory(deps: StitchedColumnsFactoryDeps): StitchedColumnsFactory {
    return (options) =>
        new StitchedColumns({
            ...options,
            market: deps.market?.entitled() ? deps.market.provider() : null,
            createTable: deps.createTable,
            departures: { timeoutMs: STITCHED_DEPARTURE_TIMEOUT_MS, schedule: scheduleTimeout },
        });
}
